/*
  Viral reels row (sections/joyvo-pdp-viral.liquid): an endless row. The script puts a copy of all the cards before and after the
  real ones (the copies are hidden from screen readers and skipped by the keyboard) and starts in the middle copy. Whenever the row
  gets close to either end it silently jumps by exactly one copy's width, which looks identical, so it never runs out.
  The round arrows move the row by one card. With too few cards to fill the row, nothing is repeated and the arrows are hidden.
  Swiping and keyboard scrolling work without this script.
  For visitors who ask their device for less motion, the GIFs are replaced by a still picture of their first frame (a GIF cannot be paused).
*/
class JoyvoReels extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.jvr__track');
    this.prev = this.querySelector('[data-jvr-prev]');
    this.next = this.querySelector('[data-jvr-next]');
    if (!this.track) return;
    this.originals = [...this.track.querySelectorAll('.jvr__card')];
    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => this.loop(), { passive: true });
    this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.still) {
      this.track.addEventListener('load', (event) => this.freeze(event.target), true);
      this.track.querySelectorAll('.jvr__gif').forEach((img) => this.freeze(img));
    }
    let timer;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(() => this.setup(), 200); });
    this.setup();
  }

  setup() {
    this.track.querySelectorAll('[data-jvr-clone]').forEach((clone) => clone.remove());
    this.looping = false;
    const first = this.originals[0];
    const last = this.originals[this.originals.length - 1];
    if (!first || !last || this.originals.length < 2) { this.arrows(false); return; }
    const gap = this.originals[1].offsetLeft - first.offsetLeft - first.offsetWidth;
    this.setWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
    // too few cards to loop: show them as they are, no arrows
    if (this.setWidth <= this.track.clientWidth * 1.15) { this.arrows(this.setWidth > this.track.clientWidth); return; }
    this.arrows(true);
    const copy = () => this.originals.map((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('data-jvr-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      return clone;
    });
    this.track.prepend(...copy());
    this.track.append(...copy());
    this.jump(this.setWidth - this.track.scrollLeft);
    this.looping = true;
  }

  arrows(show) {
    if (this.prev) this.prev.hidden = !show;
    if (this.next) this.next.hidden = !show;
  }

  // jump without animation and without the snap fighting it
  jump(delta) {
    const track = this.track;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.scrollLeft += delta;
    requestAnimationFrame(() => { track.style.scrollSnapType = ''; track.style.scrollBehavior = ''; });
  }

  loop() {
    if (!this.looping) return;
    const left = this.track.scrollLeft;
    if (left < this.setWidth * 0.25) this.jump(this.setWidth);
    else if (left > this.setWidth * 1.75) this.jump(-this.setWidth);
  }

  // swap a loaded GIF for a still picture of its first frame
  freeze(img) {
    if (!img.matches || !img.matches('img.jvr__gif') || !img.complete || !img.naturalWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.className = img.className;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', img.alt);
    canvas.getContext('2d').drawImage(img, 0, 0);
    img.replaceWith(canvas);
  }

  // one card at a time: the distance from one card to the next (card width plus the gap)
  move(direction) {
    const a = this.originals[0];
    const b = this.originals[1];
    const step = a && b ? b.offsetLeft - a.offsetLeft : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: direction * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
}
if (!customElements.get('joyvo-reels')) customElements.define('joyvo-reels', JoyvoReels);
